/* Size guide pop-up. Loaded by snippets/size-guide-modal.liquid, no dependencies.

   What it does:
   - opens the dialog from any control with data-size-guide-open, closes on the close button, on
     Escape and on a click on the backdrop, keeps focus inside and returns it to the opener;
   - switches the displayed units between metric and imperial, including the numbers in the table,
     which carry their source value in data-raw;
   - suggests a size from the supplier ranges in the size_chart metafield: pick the row the weight
     or the chest measurement falls in, then move one size up or down when the height falls outside
     that row's height range. Which of the two it asks for follows the chart: weight when the rows
     carry weight_kg, chest when they carry chest_cm instead, because a supplier who gives a body
     chart in centimetres gives no weight. No BMI, no bands of our own, no number that is not in the
     chart. Outside the chart it says so and points to the support mailbox.

   Without JavaScript the dialog never opens, the fit finder stays hidden and the size guide link
   in the mobile menu keeps working as a link to the size guide page. */
(function () {
  'use strict';

  var dialog = document.querySelector('[data-size-guide]');
  if (!dialog) return;

  /* Every word comes from window.storeStrings, which layout/theme.liquid renders from the locale
     files. A word between percent signs, such as %size%, is a placeholder that fill() replaces. */
  var S = window.storeStrings || {};

  var closeBtn = dialog.querySelector('[data-size-guide-close]');
  var finder = dialog.querySelector('[data-size-guide-finder]');
  var unitSwitch = dialog.querySelector('[data-size-guide-unit-switch]');
  var resultEl = dialog.querySelector('[data-size-guide-result]');
  var chartEl = dialog.querySelector('script[data-size-guide-chart]');
  var chartUnit = (dialog.getAttribute('data-chart-unit') || 'cm').toLowerCase();
  var sizeIndex = parseInt(dialog.getAttribute('data-size-index'), 10);
  var email = dialog.getAttribute('data-email') || '';
  var display = dialog.getAttribute('data-units') === 'imperial' ? 'imperial' : 'metric';
  var opener = null;
  var focusAfterClose = null;

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var chart = null;
  try {
    chart = chartEl ? JSON.parse(chartEl.textContent) : null;
  } catch (err) {
    chart = null;
  }

  /* ---- helpers ------------------------------------------------------------------------- */
  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text) node.textContent = text;
    return node;
  }

  function fill(text, values) {
    return String(text).replace(/%(\w+)%/g, function (match, name) {
      return Object.prototype.hasOwnProperty.call(values, name) ? values[name] : match;
    });
  }

  function isRange(value) {
    return Object.prototype.toString.call(value) === '[object Array]' && value.length === 2 && isFinite(value[0]) && isFinite(value[1]);
  }

  /* Which second measurement the chart carries next to the height range. Weight keeps priority, so
     a chart that holds both behaves exactly as it did before the chest ranges existed. */
  function chartKey() {
    if (!chart || !chart.rows || !chart.rows.length) return null;
    var keys = ['weight_kg', 'chest_cm'];
    for (var k = 0; k < keys.length; k++) {
      for (var i = 0; i < chart.rows.length; i++) {
        var row = chart.rows[i];
        if (row && row.size && isRange(row.height_cm) && isRange(row[keys[k]])) return keys[k];
      }
    }
    return null;
  }

  function usableRows() {
    var key = chartKey();
    if (!key) return [];
    var out = [];
    for (var i = 0; i < chart.rows.length; i++) {
      var row = chart.rows[i];
      if (row && row.size && isRange(row.height_cm) && isRange(row[key])) out.push(row);
    }
    return out;
  }

  function round(value, decimals) {
    var factor = Math.pow(10, decimals);
    return String(Math.round(value * factor) / factor);
  }

  function convertNumbers(text, factor, decimals) {
    return String(text).replace(/\d+(?:[.,]\d+)?/g, function (match) {
      var n = parseFloat(match.replace(',', '.'));
      if (!isFinite(n)) return match;
      return round(n * factor, decimals);
    });
  }

  /* ---- open and close ------------------------------------------------------------------ */
  function openDialog(trigger) {
    if (dialog.open) return;
    opener = trigger || document.activeElement;
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.classList.add('is-fallback');
      dialog.setAttribute('open', '');
    }
    document.body.classList.add('size-guide-open');
    if (closeBtn) closeBtn.focus();
    onOpenFit();
  }

  function closeDialog() {
    if (!dialog.open) return;
    if (typeof dialog.close === 'function' && !dialog.classList.contains('is-fallback')) {
      dialog.close();
      return;
    }
    dialog.removeAttribute('open');
    cleanup();
  }

  function cleanup() {
    document.body.classList.remove('size-guide-open');
    var target = focusAfterClose || opener;
    focusAfterClose = null;
    opener = null;
    if (target && typeof target.focus === 'function') {
      try {
        target.focus({ preventScroll: true });
      } catch (err) {
        target.focus();
      }
    }
  }

  function trapFocus(event) {
    var nodes = [];
    var all = dialog.querySelectorAll(FOCUSABLE);
    for (var i = 0; i < all.length; i++) {
      if (!all[i].hidden && all[i].offsetParent !== null) nodes.push(all[i]);
    }
    if (!nodes.length) return;
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  dialog.addEventListener('close', cleanup);

  /* ---- units --------------------------------------------------------------------------- */
  function setUnits(next) {
    display = next === 'imperial' ? 'imperial' : 'metric';
    var buttons = dialog.querySelectorAll('[data-size-guide-units]');
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].setAttribute('aria-pressed', buttons[i].getAttribute('data-size-guide-units') === display ? 'true' : 'false');
    }
    var groups = dialog.querySelectorAll('[data-size-guide-fields]');
    for (var j = 0; j < groups.length; j++) {
      groups[j].hidden = groups[j].getAttribute('data-size-guide-fields') !== display;
    }
    convertTable();
  }

  function convertTable() {
    var toImperial = chartUnit === 'cm' && display === 'imperial';
    var toMetric = chartUnit === 'in' && display === 'metric';
    var cells = dialog.querySelectorAll('[data-size-guide-cell]');
    for (var i = 0; i < cells.length; i++) {
      var raw = cells[i].getAttribute('data-raw') || '';
      if (toImperial) cells[i].textContent = convertNumbers(raw, 1 / 2.54, 1);
      else if (toMetric) cells[i].textContent = convertNumbers(raw, 2.54, 0);
      else cells[i].textContent = raw;
    }
    var heads = dialog.querySelectorAll('[data-size-guide-head]');
    for (var j = 0; j < heads.length; j++) {
      var label = heads[j].getAttribute('data-raw') || '';
      if (toImperial) label = label.replace(/\bcm\b/gi, S.sizeGuideUnitIn).replace(/\bcentimetres\b/gi, S.sizeGuideInches);
      /* Only the unit in brackets, so a heading like "Length in body" keeps its wording. */
      if (toMetric) label = label.replace(/\binches\b/gi, S.sizeGuideCentimetres).replace(/\(in\)/gi, '(cm)');
      heads[j].textContent = label;
    }
  }

  /* ---- fit finder ---------------------------------------------------------------------- */
  function readField(key) {
    var input = dialog.querySelector('[data-size-guide-input="' + key + '"]');
    if (!input || input.value === '') return null;
    var n = parseFloat(String(input.value).replace(',', '.'));
    return isFinite(n) ? n : null;
  }

  /* Both shapes hand back the height in centimetres and the second measurement in the unit the
     chart uses: kilograms for a weight chart, centimetres for a chest chart. */
  function readBody(key) {
    if (display === 'imperial') {
      var feet = readField('height_ft');
      var inches = readField('height_in');
      var second = key === 'chest_cm' ? readField('chest_in') : readField('weight_lb');
      if (feet === null || second === null) return null;
      if (inches === null) inches = 0;
      return {
        height: (feet * 12 + inches) * 2.54,
        measure: key === 'chest_cm' ? second * 2.54 : second / 2.20462
      };
    }
    var cm = readField('height_cm');
    var metric = key === 'chest_cm' ? readField('chest_cm') : readField('weight_kg');
    if (cm === null || metric === null) return null;
    return { height: cm, measure: metric };
  }

  function findSizeControl(size) {
    if (!(sizeIndex >= 0)) return null;
    var wanted = String(size).trim().toLowerCase();
    var select = document.querySelector('select[data-option-index="' + sizeIndex + '"]');
    if (select) {
      for (var i = 0; i < select.options.length; i++) {
        var option = select.options[i];
        var byValue = String(option.value).trim().toLowerCase();
        var byText = String(option.textContent).trim().toLowerCase();
        if (byValue === wanted || byText === wanted) return { type: 'select', el: select, option: option };
      }
      return null;
    }
    var radios = document.querySelectorAll('input[data-option-index="' + sizeIndex + '"]');
    for (var j = 0; j < radios.length; j++) {
      if (String(radios[j].value).trim().toLowerCase() === wanted) return { type: 'radio', el: radios[j] };
    }
    return null;
  }

  function addChooseButton(size) {
    if (!findSizeControl(size)) return;
    var button = el('button', 'btn btn--ghost btn--small sg__choose', fill(S.sizeGuideChooseSize, { size: size }));
    button.type = 'button';
    button.setAttribute('data-size-guide-choose', size);
    resultEl.appendChild(button);
  }

  function applySize(size) {
    var control = findSizeControl(size);
    if (!control) {
      closeDialog();
      return;
    }
    if (control.type === 'select') control.el.value = control.option.value;
    else control.el.checked = true;
    control.el.dispatchEvent(new Event('change', { bubbles: true }));
    focusAfterClose = control.el;
    closeDialog();
  }

  function showMessage(text) {
    resultEl.innerHTML = '';
    resultEl.appendChild(el('p', '', text));
  }

  function showOutOfRange() {
    resultEl.innerHTML = '';
    var p = el('p', '', '');
    p.appendChild(document.createTextNode(S.sizeGuideOutOfRange + ' '));
    if (email) {
      p.appendChild(document.createTextNode(S.sizeGuideOutOfRangeEmail + ' '));
      var mail = el('a', '', email);
      mail.href = 'mailto:' + email;
      p.appendChild(mail);
    } else {
      var contact = el('a', '', S.contactUs);
      contact.href = '/pages/contact';
      p.appendChild(contact);
    }
    p.appendChild(document.createTextNode(' ' + S.sizeGuideOutOfRangeSuffix));
    resultEl.appendChild(p);
  }

  function showSize(size) {
    resultEl.innerHTML = '';
    resultEl.appendChild(el('span', 'sg__badge', size));
    resultEl.appendChild(el('p', '', S.sizeGuideResultSingle));
    addChooseButton(size);
  }

  function showBetween(lower, upper) {
    resultEl.innerHTML = '';
    resultEl.appendChild(el('span', 'sg__badge', fill(S.sizeGuideBetweenSizes, { lower: lower, upper: upper })));
    resultEl.appendChild(el('p', '', S.sizeGuideResultBetween));
    addChooseButton(upper);
  }

  /* A chest measurement can land in the gap between two supplier ranges, and that is a real
     answer: name the two sizes it sits between. Below the smallest or above the largest range
     there is nothing to say, and a weight that matches no row stays outside the chart as before. */
  function gapBetween(rows, key, value) {
    for (var i = 0; i < rows.length - 1; i++) {
      if (value > rows[i][key][1] && value < rows[i + 1][key][0]) return i;
    }
    return -1;
  }

  function suggest() {
    if (!resultEl) return;
    var key = chartKey();
    var rows = usableRows();
    if (!rows.length) return;
    var body = readBody(key);
    if (!body || body.height <= 0 || body.measure <= 0) {
      showMessage(key === 'chest_cm' ? S.sizeGuideIntroChest : S.sizeGuideIntroWeight);
      return;
    }
    var index = -1;
    for (var i = 0; i < rows.length; i++) {
      if (body.measure >= rows[i][key][0] && body.measure <= rows[i][key][1]) {
        index = i;
        break;
      }
    }
    if (index === -1) {
      var gap = key === 'chest_cm' ? gapBetween(rows, key, body.measure) : -1;
      if (gap === -1) {
        showOutOfRange();
        return;
      }
      showBetween(rows[gap].size, rows[gap + 1].size);
      return;
    }
    var row = rows[index];
    var other = -1;
    if (body.height > row.height_cm[1] && index < rows.length - 1) other = index + 1;
    else if (body.height < row.height_cm[0] && index > 0) other = index - 1;
    if (other === -1) {
      showSize(row.size);
      return;
    }
    showBetween(rows[Math.min(index, other)].size, rows[Math.max(index, other)].size);
  }

  /* ---- events -------------------------------------------------------------------------- */
  document.addEventListener('click', function (event) {
    if (!event.target || !event.target.closest) return;
    var trigger = event.target.closest('[data-size-guide-open]');
    if (trigger) {
      event.preventDefault();
      /* The mobile menu holds one of these controls; leaving the drawer open behind the pop-up
         would put the visitor back in the menu when they close it. */
      var drawer = trigger.closest('details[data-menu]');
      if (drawer) drawer.removeAttribute('open');
      openDialog(trigger);
      return;
    }
    if (!dialog.open) return;
    if (event.target.closest('[data-size-guide-close]')) {
      event.preventDefault();
      closeDialog();
      return;
    }
    if (event.target === dialog) {
      closeDialog();
      return;
    }
    var unitButton = event.target.closest('[data-size-guide-units]');
    if (unitButton) {
      setUnits(unitButton.getAttribute('data-size-guide-units'));
      return;
    }
    if (event.target.closest('[data-size-guide-submit]')) {
      event.preventDefault();
      suggest();
      return;
    }
    var choose = event.target.closest('[data-size-guide-choose]');
    if (choose) {
      event.preventDefault();
      applySize(choose.getAttribute('data-size-guide-choose'));
    }
  });

  /* Escape is closed here rather than left to the browser, so the fallback path and the native
     dialog behave the same and the body scroll lock is always released. */
  document.addEventListener('keydown', function (event) {
    if (!dialog.open) return;
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      closeDialog();
      return;
    }
    if (event.key === 'Tab') trapFocus(event);
  });

  dialog.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    if (event.target && event.target.getAttribute && event.target.getAttribute('data-size-guide-input') !== null) {
      event.preventDefault();
      suggest();
    }
  });

  /* ---- jeans size advice (snippets/fit-advisor.liquid) --------------------------------------
     Garment measurements per size from custom.garment_measurements. The shopper gives height, weight and body
     shape, or her own waist and hips. Waist and hips are estimated from height and weight, the shape moves the
     balance between the two, and every size of this model is checked: the waistband must close (with the ease this
     denim needs: rigid denim a little room, stretch denim can be a little smaller), the hips must fit, and an
     elastic waist fits up to its stretched size. The first size that fits is the advice; when the margin is smaller
     than the uncertainty of the estimate, the answer is two sizes with what decides between them. The profile is
     kept in this browser (localStorage), so every next product page names her size under the sizes. */
  var fitRoot = dialog.querySelector('[data-fit]');
  var fitData = null;
  var FT = {};
  try {
    fitData = JSON.parse((dialog.querySelector('script[data-fit-data]') || {}).textContent || 'null');
    FT = JSON.parse((dialog.querySelector('script[data-fit-strings]') || {}).textContent || '{}');
  } catch (err) {
    fitData = null;
  }

  /* The model (google-stores/store-15/research/taglie-en-maatadvies-2026-09-27.md, "Formules"):
     hips from weight and height with the published Hip Index exponents (weight^0.482, height^-0.310) and 103 cm at
     73 kg and 166 cm; waist from hips along the German body size table (1.10 x hips - 33.5), which rises 2.17 cm per
     BMI point, close to the published 2.22 cm (Bozeman). One standard deviation is about 4-5 cm on the hips and 6 cm
     on the waist, so two sizes are often close; the advice still names one. The build (slim, medium, full) moves waist
     and hips together (cm, [waist, hip]).
     Rise: where the waistband sits between waist and hips, measured on our own charts (low rise measures about 4.5 cm
     more than natural rise at the same size). Ease: the room each denim needs at waist and hips (rigid a little
     more than the body, stretch denim smaller than the body, the research values for stretch and close stretch); an
     elastic waist sits comfortably up to elasticUse of its stretch range. Hem: crotch height is about 0.47 x body
     height; the garment
     hangs "drop" cm below the crotch. */
  var MODEL = {
    hip: function (h, w) { return 103 * Math.pow(w / 73, 0.482) * Math.pow(h / 166, -0.31); },
    waist: function (hip) { return 1.1 * hip - 33.5; },
    shape: { snella: [-2, -2], media: [0, 0], morbida: [2.5, 2.5] },
    rise: { high: 0, natural: 0, low: 0.2 },
    waistEase: { none: 1, light: -1, medium: -2, high: -3 },
    hipEase: { none: 3, light: 0, medium: -2, high: -3 },
    elasticUse: 0.6,
    between: 2,
    crotch: 0.47,
    drop: { baggy: 5, wide: 5, palazzo: 5, barrel: 4, straight: 2 }
  };
  var PROFILE_KEY = 'fit-profile-v1';

  function readProfile() {
    try {
      var p = JSON.parse(window.localStorage.getItem(PROFILE_KEY) || 'null');
      return p && typeof p === 'object' ? p : null;
    } catch (err) {
      return null;
    }
  }
  function saveProfile(p) {
    try { window.localStorage.setItem(PROFILE_KEY, JSON.stringify(p)); } catch (err) { /* private mode: advice still shows */ }
  }

  function fitInputs() {
    var out = {};
    if (!fitRoot) return out;
    var fields = fitRoot.querySelectorAll('[data-fit-input]');
    for (var i = 0; i < fields.length; i++) {
      var f = fields[i];
      var key = f.getAttribute('data-fit-input');
      if (f.type === 'radio') {
        if (f.checked) out[key] = f.value;
      } else if (f.value !== '') {
        var n = parseFloat(String(f.value).replace(',', '.'));
        if (isFinite(n) && n > 0) out[key] = n;
      }
    }
    return out;
  }

  function fillInputs(p) {
    if (!fitRoot || !p) return;
    var fields = fitRoot.querySelectorAll('[data-fit-input]');
    var shapeKnown = !!MODEL.shape[p.shape];
    for (var i = 0; i < fields.length; i++) {
      var f = fields[i];
      var key = f.getAttribute('data-fit-input');
      /* A build from an older profile that no longer exists falls back to the middle one. */
      if (f.type === 'radio') f.checked = shapeKnown ? p[key] === f.value : f.value === 'media';
      else if (p[key] != null) f.value = p[key];
    }
    var own = fitRoot.querySelector('[data-fit-own]');
    if (own && (p.waist || p.hip)) own.open = true;
  }

  /* Waist and hips in cm: her own measurements when she gave both, otherwise from height, weight and build. */
  function bodyOf(p) {
    if (!p) return null;
    if (p.waist && p.hip) return { waist: p.waist, hip: p.hip, own: true };
    if (!p.height || !p.weight || p.height < 130 || p.height > 210 || p.weight < 30 || p.weight > 200) return null;
    var hip = MODEL.hip(p.height, p.weight);
    var waist = MODEL.waist(hip);
    var shift = MODEL.shape[p.shape];
    if (shift) {
      waist += shift[0];
      hip += shift[1];
    }
    /* One measurement of her own replaces its counterpart from the model. */
    if (p.waist) waist = p.waist;
    if (p.hip) hip = p.hip;
    return { waist: waist, hip: hip, own: false };
  }

  function colourRows() {
    if (!fitData) return [];
    var section = document.querySelector('[data-product]');
    var ci = section ? parseInt(section.getAttribute('data-color-index'), 10) : -1;
    if (fitData.per_colour && ci >= 0) {
      var checked = document.querySelector('input[data-option-index="' + ci + '"]:checked');
      if (checked && fitData.per_colour[checked.value]) return fitData.per_colour[checked.value];
    }
    return fitData.rows || [];
  }

  function advise(body, rows) {
    var fit = fitData.fit || {};
    var stretch = fit.stretch || 'none';
    var we = MODEL.waistEase[stretch] != null ? MODEL.waistEase[stretch] : 1;
    var he = MODEL.hipEase[stretch] != null ? MODEL.hipEase[stretch] : 3;
    var band = body.waist + (MODEL.rise[fit.rise] || 0) * (body.hip - body.waist);
    var m = rows.map(function (r) {
      var mw = r.waist_max_cm ? (r.waist_cm + MODEL.elasticUse * (r.waist_max_cm - r.waist_cm)) - band : r.waist_cm - band - we;
      /* Stretch models in this range are cut loose, yet their charts give hips no loose jeans has (F02, F07, F16:
         "wide legs, loose fit" with 85 cm hips in S), so with stretch the waist decides alone. */
      var mh = r.hip_cm && (stretch === 'none' || stretch === 'light') ? r.hip_cm - body.hip - he : 99;
      return { row: r, mw: mw, mh: mh, m: Math.min(mw, mh) };
    });
    var i = -1;
    for (var k = 0; k < m.length; k++) {
      if (m[k].m >= 0) { i = k; break; }
    }
    var res = { band: band, body: body, rows: rows, margins: m, stretch: stretch, elastic: false };
    if (i === -1) {
      res.type = 'tooBig';
      res.row = rows[rows.length - 1];
      return res;
    }
    res.type = 'single';
    res.row = rows[i];
    res.index = i;
    if (m[i].m < MODEL.between && i < rows.length - 1) {
      res.type = 'between';
      res.lower = rows[i];
      res.upper = rows[i + 1];
    } else if (i > 0 && m[i - 1].m > -MODEL.between) {
      res.type = 'between';
      res.lower = rows[i - 1];
      res.upper = rows[i];
    }
    /* One advice, always: between two, rigid denim takes the larger, stretch denim or an elastic waist the smaller,
       and the other one is offered as a second choice. */
    res.elastic = !!(res.type === 'between' && res.lower.waist_max_cm);
    res.pick = res.type === 'between' ? (stretch === 'none' && !res.elastic ? res.upper : res.lower) : res.row;
    res.alt = res.type === 'between' ? (res.pick === res.upper ? res.lower : res.upper) : null;
    res.pickMargin = m[rows.indexOf(res.pick)];
    res.loose = res.pickMargin && res.pickMargin.mw > 8 && res.pickMargin.mh < 4;
    res.tooSmall = i === 0 && m[0].mw > 10 && m[0].mh > 6;
    return res;
  }

  function shortSize(size) {
    return String(size).split(' (')[0];
  }

  function subSize(size) {
    var s = String(size);
    var i = s.indexOf(' (');
    return i > -1 ? s.slice(i + 2).replace(/\)$/, '') : '';
  }

  /* Where the hem falls: centimetres above the floor = crotch height - drop - inseam. At or below 1 cm it reaches the
     shoe (or rolls up), up to 9 cm the ankle, higher it stops above the ankle. */
  function lengthLine(row, height) {
    if (!row || !row.inseam_cm || !height) return '';
    var drop = MODEL.drop[(fitData.fit || {}).type] || 3;
    var above = MODEL.crotch * height - drop - row.inseam_cm;
    return above <= 1 ? FT.lengthLong : above <= 9 ? FT.lengthOk : FT.lengthShort;
  }

  /* How the advised size sits, in words: the waist comfortable, just right or close; the hips roomy, comfortable or
     close. Her own measurements show as numbers next to the jeans' numbers. */
  function fitLines(res) {
    var row = res.pick;
    var pm = res.pickMargin || { mw: 0, mh: 99 };
    var lines = [];
    if (res.body.own) {
      lines.push(row.waist_max_cm
        ? fill(FT.waistElasticOwn, { min: row.waist_cm, max: row.waist_max_cm, body: Math.round(res.band) })
        : fill(FT.waistLine, { garment: row.waist_cm, body: Math.round(res.band) }));
      if (row.hip_cm) lines.push(fill(FT.hipLine, { garment: row.hip_cm, body: Math.round(res.body.hip) }));
      return lines;
    }
    var wv = pm.mw >= 5 ? FT.fitComfyF : pm.mw >= 2 ? FT.fitRightF : FT.fitCloseF;
    lines.push(row.waist_max_cm
      ? fill(FT.waistElastic, { min: row.waist_cm, max: row.waist_max_cm, verdict: wv })
      : fill(FT.waistFit, { garment: row.waist_cm, verdict: wv }));
    if (row.hip_cm && pm.mh < 99) {
      var hv = pm.mh >= 8 ? FT.fitRoomyM : pm.mh >= 3 ? FT.fitComfyM : FT.fitCloseM;
      lines.push(fill(FT.hipFit, { garment: row.hip_cm, verdict: hv }));
    }
    return lines;
  }

  function renderWait() {
    var box = fitRoot && fitRoot.querySelector('[data-fit-result]');
    if (!box) return;
    box.innerHTML = '';
    box.appendChild(el('p', 'fit__wait', FT.missing));
  }

  function renderAdvice(res, p) {
    var box = fitRoot && fitRoot.querySelector('[data-fit-result]');
    if (!box) return;
    if (!res) { renderWait(); return; }
    box.innerHTML = '';
    var card = el('div', 'fit__answer', '');
    if (res.type === 'tooBig') {
      /* A regular model that ends below EU 48 points to the plus size line, which goes up to EU 54 (IT 58). */
      var maxEu = parseInt(String(res.row.eu || '').split('/').pop(), 10);
      card.appendChild(el('p', 'fit__note', fill(maxEu && maxEu < 48 ? FT.tooBigCurvy : FT.tooBig, { size: res.row.size })));
      box.appendChild(card);
      return;
    }
    card.appendChild(el('p', 'fit__label', FT.adviceLabel));
    var pick = el('p', 'fit__pick', '');
    pick.appendChild(el('span', 'fit__pick-size', shortSize(res.pick.size)));
    var sub = subSize(res.pick.size);
    if (sub) pick.appendChild(el('span', 'fit__pick-sub', sub));
    card.appendChild(pick);
    card.appendChild(el('p', 'fit__lines', fitLines(res).join(' · ')));
    var len = lengthLine(res.pick, p && p.height);
    if (len) card.appendChild(el('p', 'fit__lines', len));
    if (res.type === 'between') {
      card.appendChild(el('p', 'fit__note', fill(res.stretch === 'none' && !res.elastic ? FT.betweenRigid : FT.betweenStretch, { lower: shortSize(res.lower.size), upper: shortSize(res.upper.size) })));
    }
    if (res.loose) card.appendChild(el('p', 'fit__note', FT.looseWaist));
    if (res.tooSmall) card.appendChild(el('p', 'fit__note', fill(FT.tooSmall, { size: shortSize(res.row.size) })));
    if (findSizeControl(res.pick.size)) {
      var b = el('button', 'btn btn--full sg__choose', fill(FT.choose, { size: shortSize(res.pick.size) }));
      b.type = 'button';
      b.setAttribute('data-size-guide-choose', res.pick.size);
      card.appendChild(b);
    }
    if (res.alt && findSizeControl(res.alt.size)) {
      var a = el('button', 'fit__alt', fill(FT.chooseAlt, { size: shortSize(res.alt.size) }));
      a.type = 'button';
      a.setAttribute('data-size-guide-choose', res.alt.size);
      card.appendChild(a);
    }
    card.appendChild(el('p', 'fit__reassure', FT.reassure));
    box.appendChild(card);
  }

  /* The line under the sizes and the mark on the advised size. */
  function renderHint(res, p) {
    var hint = document.querySelector('[data-fit-hint]');
    var marked = document.querySelectorAll('.is-advised');
    for (var i = 0; i < marked.length; i++) marked[i].classList.remove('is-advised');
    if (!hint) return;
    hint.innerHTML = '';
    var open = el('button', 'fit-hint__open', '');
    open.type = 'button';
    open.setAttribute('data-size-guide-open', '');
    open.setAttribute('aria-haspopup', 'dialog');
    open.setAttribute('aria-controls', 'SizeGuideDialog');
    if (!res || res.type === 'tooBig') {
      open.textContent = FT.hintFind;
      hint.appendChild(open);
      hint.hidden = false;
      return;
    }
    hint.appendChild(el('strong', '', fill(FT.hintFor, { size: res.pick.size })));
    var basis = res.body.own
      ? fill(FT.hintBasisOwn, { waist: Math.round(p.waist), hip: Math.round(p.hip) })
      : fill(FT.hintBasis, { height: Math.round(p.height), weight: Math.round(p.weight) });
    hint.appendChild(el('span', 'fit-hint__basis', basis));
    open.textContent = FT.hintEdit;
    hint.appendChild(open);
    hint.hidden = false;
    var control = findSizeControl(res.pick.size);
    if (control && control.type === 'radio') {
      var label = document.querySelector('label[for="' + control.el.id + '"]');
      if (label) {
        label.classList.add('is-advised');
        label.setAttribute('data-advised', FT.advised);
      }
    }
  }

  /* Opening the pop-up with a remembered profile fills the fields and shows the advice for this model at once. */
  function onOpenFit() {
    if (!fitRoot || !fitData) return;
    var p = readProfile();
    if (!p) return;
    fillInputs(p);
    refreshFit(true);
  }

  function renderRows(rows) {
    var body = fitRoot && fitRoot.querySelector('[data-fit-rows]');
    if (!body || !rows.length) return;
    var trs = body.querySelectorAll('tr');
    /* Same sizes in the same order for every colour: only the numbers change, so the cells are refreshed in place. */
    if (trs.length !== rows.length) return;
    for (var i = 0; i < rows.length; i++) {
      var r = rows[i];
      var cells = trs[i].querySelectorAll('td');
      var values = [r.waist_max_cm ? r.waist_cm + '–' + r.waist_max_cm : r.waist_cm, r.hip_cm];
      var extra = [r.thigh_cm, r.inseam_cm, r.length_cm];
      for (var e = 0; e < extra.length && values.length < cells.length; e++) values.push(extra[e] == null ? '–' : extra[e]);
      for (var c = 0; c < cells.length && c < values.length; c++) cells[c].textContent = values[c];
    }
  }

  function refreshFit(show) {
    if (!fitData) return;
    var p = readProfile();
    var rows = colourRows();
    renderRows(rows);
    var body = bodyOf(p);
    var res = body && rows.length ? advise(body, rows) : null;
    renderHint(res, p);
    if (show) renderAdvice(res, p);
  }

  /* The advice follows the fields as she types: no button to press. Height and weight complete (or her own waist and
     hips) is enough; the profile is kept and the line under the sizes updates with it. */
  var liveTimer = null;
  function onFitInput() {
    clearTimeout(liveTimer);
    liveTimer = setTimeout(function () {
      var p = fitInputs();
      if (!bodyOf(p)) { renderWait(); return; }
      saveProfile(p);
      refreshFit(true);
    }, 250);
  }

  if (fitRoot && fitData) {
    var form = fitRoot.querySelector('[data-fit-form]');
    if (form) {
      form.addEventListener('submit', function (event) { event.preventDefault(); onFitInput(); });
      form.addEventListener('input', onFitInput);
      form.addEventListener('change', onFitInput);
    }
    /* A colour with its own measurements (per_colour) changes the table and the advice. */
    document.addEventListener('change', function (event) {
      if (event.target && event.target.matches && event.target.matches('input[data-option-index]')) refreshFit(false);
    });
    refreshFit(false);
  }

  /* ---- start --------------------------------------------------------------------------- */
  if (unitSwitch) unitSwitch.hidden = false;
  if (finder && usableRows().length) finder.hidden = false;
  convertTable();
})();
